import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ReactNode, useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Invoice } from '~hooks/useInvoices/models';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import LineItemsTable from '~pages/Approvals/DriverPay/LineItemsTable';
import VendorRatePanel from '~pages/Approvals/DriverPay/VendorRatePanel';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

interface SettlementInvoiceEditDialogProps {
  invoice: Invoice;
  isSettlementApproved?: boolean;
  notice?: ReactNode;
  onClose: () => void;
  onRateChanged?: () => void;
  rateCategory?: InvoiceCategoryFilter;
}

type EditedPart = 'lines' | 'rate';

export default function SettlementInvoiceEditDialog({
  invoice,
  isSettlementApproved,
  notice,
  onClose,
  onRateChanged,
  rateCategory,
}: SettlementInvoiceEditDialogProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { currencyFormatter } = useCompanyCurrency();
  const { getJobById } = useJob();
  // getJobById is rebuilt on every render, so the effect below reads it from here
  const getJobByIdRef = useRef(getJobById);
  getJobByIdRef.current = getJobById;
  const [job, setJob] = useState<Job | null>(null);
  const [hasJobReadFailed, setHasJobReadFailed] = useState(false);
  const [jobReadCount, setJobReadCount] = useState(0);
  const [editedPart, setEditedPart] = useState<EditedPart | null>(null);
  const isEditing = editedPart !== null;
  const trackEditing = (part: EditedPart) => (isEditingPart: boolean) =>
    setEditedPart(isEditingPart ? part : null);
  const utils = {
    approveInvoice: () => Promise.resolve(),
    currencyFormatter,
    unapproveInvoice: () => Promise.resolve(),
  };

  const jobId = invoice.job?.id;

  useEffect(() => {
    const controller = new AbortController();

    const loadJob = async () => {
      if (!jobId) {
        return;
      }

      setHasJobReadFailed(false);
      try {
        const readJob = await getJobByIdRef.current(jobId, companyId, controller.signal);

        if (!controller.signal.aborted) {
          setJob(readJob ?? null);
        }
      } catch {
        if (!controller.signal.aborted) {
          setHasJobReadFailed(true);
        }
      }
    };

    void loadJob();

    return () => controller.abort();
  }, [jobId, companyId, jobReadCount]);

  return (
    <Dialog isOpen maxWidth="lg" onClose={isEditing ? undefined : onClose}>
      <DialogHeader onClose={isEditing ? undefined : onClose}>
        {t('approvals.driver_pay.settlements.lines.edit_dialog_title', {
          name: invoice.job?.jobId ?? invoice.id,
        })}
      </DialogHeader>
      <DialogContent>
        {notice}
        {job ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rateCategory && (
              <VendorRatePanel
                canEdit={editedPart !== 'lines' && isSettlementApproved !== true}
                category={rateCategory}
                hideInvoiceApproval
                invoice={invoice}
                isSettlementApproved={isSettlementApproved}
                job={job}
                onEditChange={trackEditing('rate')}
                reload={onRateChanged}
                utils={utils}
              />
            )}
            <LineItemsTable
              canEdit={editedPart !== 'rate'}
              invoice={invoice}
              job={job}
              onEditChange={trackEditing('lines')}
              utils={utils}
            />
          </Box>
        ) : hasJobReadFailed ? (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, p: 1 }}>
            <Typography sx={{ color: 'error.main' }} variant="body2">
              {t('approvals.driver_pay.settlements.lines.job_read_failed')}
            </Typography>
            <SmallButton
              onClick={() => setJobReadCount((count) => count + 1)}
              variant="outlined"
            >
              {t('actions.retry')}
            </SmallButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
